<template>
  <Card class="ContactSupport" :elevation>
    <h3 class="ContactSupport-title">
      {{ titleText }}
    </h3>

    <p class="ContactSupport-description">
      {{ descriptionText }}
    </p>

    <ul v-if="items?.length" class="ContactSupport-items">
      <li
        v-for="item of items"
        :key="item.id"
        class="ContactSupport-item"
      >
        <a
          class="ContactSupport-link"
          :href="item.href"
          :target="item.external ? '_blank' : undefined"
          :rel="item.external ? 'noopener noreferrer' : undefined"
        >
          <span class="ContactSupport-itemIcon">
            <Icon :icon="item.icon" :size="18" />
          </span>

          <span class="ContactSupport-itemText">
            <span class="ContactSupport-itemLabel">{{ item.label }}</span>
            <span v-if="item.value" class="ContactSupport-itemValue">{{ item.value }}</span>
          </span>

          <Icon
            class="ContactSupport-itemChevron"
            icon="chevron-right"
            :size="16"
            flipRtl
          />
        </a>
      </li>
    </ul>
  </Card>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { useI18n } from '@cvo/i18n/composables/useI18n';
import Card from '../../atoms/Card/Card.vue';
import Icon from '../../atoms/Icon/Icon.vue';
import type { ContactSupportProps } from './ContactSupport.model';

const props = defineProps<ContactSupportProps>();

const { t } = useI18n();

// The copy is the same wherever the card appears, so the component owns it and callers
// only override when a surface needs something different.
const titleText = computed(() => props.title ?? t('ui.components.contactSupport.title'));
const descriptionText = computed(() => props.description ?? t('ui.components.contactSupport.description'));
</script>

<style lang="scss">
@use '@cvo/ui/styles/design-system' as vars;
@use '@cvo/ui/styles/utils/text';

@layer cvo-ui {
  .ContactSupport {
    display: flex;
    flex-direction: column;
    gap: vars.$dimensions-d-3;
    background-color: vars.$organisms-card-support-bg-color;
    border-color: vars.$organisms-card-support-border-color;

    &-title {
      color: vars.$organisms-card-support-title-color;
      @include text.heading('h6');
    }

    &-description {
      color: vars.$organisms-card-support-subtitle-color;
      @include text.text('sm');
    }

    &-item {
      list-style: none;

      &:not(:last-child) {
        border-block-end: vars.$dimensions-d-0-25 solid vars.$organisms-card-value-item-divider-color;
      }

      &:last-child .ContactSupport-link {
        padding-block-end: 0;
      }
    }

    &-link {
      display: flex;
      align-items: center;
      gap: vars.$dimensions-d-3;
      padding-block: vars.$dimensions-d-3;
      text-decoration: none;
    }

    &-itemIcon {
      display: flex;
      align-items: center;
      justify-content: center;
      flex-shrink: 0;
      inline-size: 36px;
      block-size: 36px;
      background-color: vars.$organisms-card-value-item-icon-bg-color;
      border-radius: vars.$dimensions-d-2;
      color: vars.$organisms-card-value-item-label-color;
    }

    &-itemText {
      display: flex;
      flex-direction: column;
      flex: 1;
      gap: vars.$dimensions-d-0-5;
      min-inline-size: 0;
      overflow-wrap: anywhere;
    }

    &-itemLabel {
      color: vars.$organisms-card-value-item-label-color;
      @include text.text('sm', vars.$typography-weight-strong);
    }

    &-itemValue {
      color: vars.$organisms-card-value-item-value-color;
      @include text.text('xs');
    }

    &-itemChevron {
      flex-shrink: 0;
      color: vars.$organisms-card-value-item-value-color;
    }
  }
}
</style>
